import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Full-viewport modal shell portaled to document.body so overlays cover the
 * fixed header (and are not clipped by layout overflow / containing blocks).
 */
const ModalShell = ({
  isOpen,
  onClose,
  children,
  className = '',
  panelClassName = '',
  labelledBy,
  /** center (default) | drawer — right-side full-height panel */
  variant = 'center',
}) => {
  const isDrawer = variant === 'drawer';

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = previous;
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className={`theme-vendalist fixed inset-0 z-[11000] flex bg-transparent ${
        isDrawer
          ? 'items-stretch justify-end'
          : `items-center justify-center ${className.includes('p-0') ? '' : 'p-4 sm:p-6'}`
      } ${className}`}
      role="dialog"
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`relative z-10 w-full text-ink ${
          isDrawer
            ? 'ml-auto flex h-full max-h-none max-w-lg flex-col overflow-hidden border-l border-border bg-surface shadow-xl'
            : `max-h-[min(92dvh,900px)] ${
                panelClassName.includes('overflow-') ? '' : 'overflow-y-auto'
              }`
        } ${panelClassName}`}
      >
        {children}
      </div>
    </div>,
    document.body
  );
};

export default ModalShell;
